import { LEAVE_STATUS, LEAVE_TYPES } from "./staffHolidayConstants";
import {
  calculateHolidayEntitlementBreakdown,
  getDatesBetween,
  getHolidayYearRange,
  isWeekend,
  parseHolidayDate,
  toDateKey,
} from "./staffHolidayDateUtils";

export const normaliseEmail = (email = "") => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

export const normalizeEmail = normaliseEmail;

const historyTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const latestHistoryTimestamp = (entry) => Math.max(
  0,
  ...[entry.updatedAt, entry.createdAt, entry.importedAt].map(historyTimestamp),
  ...(Array.isArray(entry.auditTrail) ? entry.auditTrail : [])
    .map((audit) => historyTimestamp(audit.actionedAt)),
  ...(Array.isArray(entry.trackerEvents) ? entry.trackerEvents : [])
    .map(latestHistoryTimestamp),
);

export const getLatestLeaveHistoryEntries = (entries = []) => {
  const latest = new Map();
  entries.forEach((entry, index) => {
    const start = toDateKey(entry.startDate || entry.date);
    const end = toDateKey(entry.endDate || entry.startDate || entry.date);
    // Keep different ranges: discarding a partially overlapping request would
    // also hide its other dates and the actions belonging to that request.
    const key = start && end
      ? `${getRequestStaffEmail(entry)}|${start}|${end}|${normalizeLeaveTypeValue(entry.leaveType)}`
      : `undated|${index}`;
    const previous = latest.get(key);
    const time = latestHistoryTimestamp(entry);
    const previousTime = previous ? latestHistoryTimestamp(previous) : -1;
    const tieKey = (item) => `${item.isTrackerImport ? "0" : "1"}|${item.requestId || item.id || ""}`;
    if (!previous || time > previousTime ||
      (time === previousTime && tieKey(entry) > tieKey(previous))) {
      latest.set(key, entry);
    }
  });
  return [...latest.values()];
};

export const PENDING_APPROVAL_STATUSES = [
  LEAVE_STATUS.PENDING_HOLIDAY_MANAGER,
  "pending_manager",
  "pending_holiday_manager",
  LEAVE_STATUS.PENDING_ADMIN,
]
  .filter(Boolean)
  .map((status) => String(status).toLowerCase());

export const isPendingApprovalStatus = (status = "") => {
  return PENDING_APPROVAL_STATUSES.includes(String(status || "").toLowerCase());
};

export const isApprovedStatus = (status = "") => {
  return String(status || "").toLowerCase() === LEAVE_STATUS.APPROVED;
};

export const isRejectedStatus = (status = "") => {
  return String(status || "").toLowerCase() === LEAVE_STATUS.REJECTED;
};

export const isCancelledStatus = (status = "") => {
  return String(status || "").toLowerCase() === LEAVE_STATUS.CANCELLED;
};

export const getStatusTone = (status = "") => {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized === LEAVE_STATUS.PENDING_HOLIDAY_MANAGER ||
    normalized === "pending_manager" ||
    normalized === "pending_holiday_manager"
  ) {
    return "yellow";
  }

  if (normalized === LEAVE_STATUS.PENDING_ADMIN) return "purple";
  if (normalized === LEAVE_STATUS.APPROVED) return "green";
  if (normalized === LEAVE_STATUS.REJECTED) return "red";
  if (normalized === LEAVE_STATUS.CANCELLED) return "grey";

  return "grey";
};

export const isEmailLike = (value = "") => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
};

export const getCleanName = (value = "") => {
  const name = String(value || "").trim();

  if (!name) return "";
  if (isEmailLike(name)) return "";

  return name;
};

export const formatNameFromEmail = (email = "") => {
  const localPart = String(email || "")
    .trim()
    .split("@")[0];

  if (!localPart) return "";

  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((namePart) => {
      const cleaned = String(namePart || "").trim();

      if (!cleaned) return "";

      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    })
    .filter(Boolean)
    .join(" ");
};

export const getRequestStaffEmail = (request = {}) => {
  return normaliseEmail(
    request.staffEmail ||
      request.ownerStaffEmail ||
      request.email ||
      request.staffName ||
      request.displayName ||
      "",
  );
};

export const getRequestStaffName = (request = {}) => {
  const email = getRequestStaffEmail(request);

  const fullName = [request.firstName, request.lastName]
    .map(getCleanName)
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    getCleanName(request.staffName) ||
    getCleanName(request.displayName) ||
    fullName ||
    formatNameFromEmail(email) ||
    email ||
    "Unknown staff"
  );
};

export const getStaffDirectoryDisplayName = (staff = {}) => {
  const email = normaliseEmail(staff.email || staff.id || "");

  const fullName = [staff.firstName, staff.lastName]
    .map(getCleanName)
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    getCleanName(staff.displayName) ||
    getCleanName(staff.name) ||
    fullName ||
    formatNameFromEmail(email) ||
    email ||
    staff.id ||
    "Unknown staff"
  );
};

export const formatNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return "-";

  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const hasValue = (value) => {
  return value !== undefined && value !== null && value !== "";
};

const toNumber = (value, fallback = 0) => {
  if (!hasValue(value)) return fallback;

  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

export const normalizeDepartments = (departments) => {
  if (!departments) return [];

  const list = Array.isArray(departments) ? departments : [departments];

  return list
    .map((department) => {
      if (typeof department === "string") {
        const value = department.trim();

        return value
          ? {
              id: value,
              name: value,
            }
          : null;
      }

      if (department && typeof department === "object") {
        const id = String(
          department.id ||
            department.departmentId ||
            department.value ||
            department.name ||
            department.label ||
            "",
        ).trim();

        const name = String(
          department.name ||
            department.departmentName ||
            department.label ||
            id,
        ).trim();

        return id
          ? {
              id,
              name: name || id,
            }
          : null;
      }

      return null;
    })
    .filter(Boolean);
};

export const getDepartmentName = (record = {}) => {
  const departments = normalizeDepartments(record.departments);

  return (
    String(record.departmentName || record.department || "").trim() ||
    departments
      .map((department) => department.name)
      .filter(Boolean)
      .join(", ") ||
    "No department"
  );
};

const getHolidayBalanceSource = (staff = {}) => {
  return (
    staff.holidayBalance ||
    staff.leaveBalance ||
    staff.balance ||
    staff.currentBalance ||
    {}
  );
};

export const getStaffEmploymentStartDate = (staff = {}) => {
  return (
    staff.employmentStartDate ||
    staff.startDate ||
    staff.joinDate ||
    staff.dateJoined ||
    staff.startedOn ||
    staff.holidayStartDate ||
    ""
  );
};

export const getStaffEmploymentEndDate = (staff = {}) => {
  return (
    staff.employmentEndDate ||
    staff.leavingDate ||
    staff.endDate ||
    staff.leaveDate ||
    staff.terminationDate ||
    staff.lastWorkingDate ||
    staff.holidayEndDate ||
    staff.employment?.endDate ||
    staff.profile?.employmentEndDate ||
    staff.profile?.leaveDate ||
    ""
  );
};

export const isInactiveStaff = (staff = {}, today = new Date()) => {
  const employmentEndDate = toDateKey(getStaffEmploymentEndDate(staff));
  return Boolean(employmentEndDate && employmentEndDate < toDateKey(today));
};

export const isSummaryStaffDirectoryItem = (staff = {}) =>
  Boolean(normaliseEmail(staff.email) || normaliseEmail(staff.id)) &&
  (hasStaffDepartment(staff) || isInactiveStaff(staff));

export const getStaffAnnualHolidayEntitlement = (staff = {}) => {
  const balance = getHolidayBalanceSource(staff);

  return toNumber(
    balance.annualEntitlement ??
      balance.fullAnnualEntitlement ??
      staff.annualEntitlement ??
      staff.annualLeaveEntitlement ??
      staff.totalAnnualHolidayEntitlement ??
      staff.fullAnnualEntitlement ??
      staff.entitlement ??
      balance.entitlement,
    20,
  );
};

export const normalizeHolidayBalance = (staff = {}, selectedYear) => {
  const balance = getHolidayBalanceSource(staff);

  const holidayYear = toNumber(
    selectedYear || balance.year || staff.holidayYear,
    new Date().getFullYear(),
  );

  const annualEntitlement = getStaffAnnualHolidayEntitlement(staff);

  const entitlementBreakdown = calculateHolidayEntitlementBreakdown({
    employmentStartDate: getStaffEmploymentStartDate(staff),
    employmentEndDate: getStaffEmploymentEndDate(staff),
    annualEntitlement,
    holidayYear,
    holidayYearStartMonth: 1,
    holidayYearStartDay: 1,
    rounding: "up",
  });

  return {
    year: holidayYear,

    annualEntitlement,
    entitlement: entitlementBreakdown.entitlement,
    rawEntitlement: entitlementBreakdown.rawEntitlement,

    employmentStartDate: toDateKey(entitlementBreakdown.employmentStartDate),
    employmentEndDate: toDateKey(entitlementBreakdown.employmentEndDate),
    effectiveStartDate: toDateKey(entitlementBreakdown.effectiveStartDate),
    effectiveEndDate: toDateKey(entitlementBreakdown.effectiveEndDate),
    employedDaysInHolidayYear: entitlementBreakdown.employedDaysInHolidayYear,
    totalDaysInHolidayYear: entitlementBreakdown.totalDaysInHolidayYear,

    carriedForward: toNumber(balance.carriedForward, 0),
    extraDays: toNumber(balance.extraDays, 0),
    borrowedFromNextYear: toNumber(balance.borrowedFromNextYear, 0),
    annualLeaveTaken: toNumber(balance.annualLeaveTaken, 0),
    annualLeavePending: toNumber(balance.annualLeavePending, 0),

    notes: balance.notes || "",
    importedFrom: balance.importedFrom || "",
    importedBy: balance.importedBy || "",
  };
};

export const getAnnualLeaveStats = ({
  staff = {},
  requests = [],
  selectedYear,
}) => {
  const balance = normalizeHolidayBalance(staff, selectedYear);
  const { yearStart, yearEnd } = getHolidayYearRange({
    holidayYear: selectedYear,
    holidayYearStartMonth: 1,
    holidayYearStartDay: 1,
  });
  const employmentStart =
    parseHolidayDate(balance.effectiveStartDate) || yearStart;
  const employmentEnd = parseHolidayDate(balance.effectiveEndDate) || yearEnd;
  const approvedDates = new Map();
  const pendingDates = new Map();
  const approvedRequestIdsByDate = new Map();

  const getIncludedDates = (request) => {
    if (Array.isArray(request.includedDates) && request.includedDates.length) {
      return request.includedDates
        .map((item) => ({
          date: parseHolidayDate(item?.date || item?.dateKey || item),
          value: toNumber(
            item?.dayValue ?? item?.value ?? item?.days ?? item?.totalDays,
            1,
          ),
        }))
        .filter((item) => item.date);
    }

    const startDate = parseHolidayDate(
      request.startDate || request.date || request.endDate,
    );
    const endDate = parseHolidayDate(
      request.endDate || request.startDate || request.date,
    );

    if (!startDate || !endDate) return [];

    const dates = getDatesBetween(startDate, endDate).filter(
      (date) => !isWeekend(date),
    );
    const requestedTotal = Number(request.totalDays);
    const hasExplicitDayTypes =
      (request.startDayType && request.startDayType !== "full") ||
      (request.endDayType && request.endDayType !== "full");

    if (
      !hasExplicitDayTypes &&
      Number.isFinite(requestedTotal) &&
      requestedTotal > 0 &&
      requestedTotal <= dates.length
    ) {
      let remainingValue = requestedTotal;

      return dates
        .map((date) => {
          const value = Math.min(1, remainingValue);
          remainingValue -= value;
          return { date, value };
        })
        .filter(({ value }) => value > 0);
    }

    return dates.map((date, index) => {
      const isFirst = index === 0;
      const isLast = index === dates.length - 1;
      const isHalfDay =
        (isFirst && request.startDayType && request.startDayType !== "full") ||
        (isLast && request.endDayType && request.endDayType !== "full");

      return { date, value: isHalfDay ? 0.5 : 1 };
    });
  };

  requests.forEach((request) => {
    if (request.leaveType !== LEAVE_TYPES.ANNUAL_LEAVE) return;

    const target = isApprovedStatus(request.status)
      ? approvedDates
      : isPendingApprovalStatus(request.status)
        ? pendingDates
        : null;

    if (!target) return;

    getIncludedDates(request).forEach(({ date, value }) => {
      if (date < yearStart || date > yearEnd) return;
      if (date < employmentStart || date > employmentEnd) return;

      const dateKey = toDateKey(date);
      target.set(dateKey, Math.max(target.get(dateKey) || 0, value));

      if (target === approvedDates) {
        if (!approvedRequestIdsByDate.has(dateKey)) {
          approvedRequestIdsByDate.set(dateKey, new Set());
        }

        approvedRequestIdsByDate
          .get(dateKey)
          .add(request.requestId || request.id || "(missing Firebase ID)");
      }
    });
  });

  approvedDates.forEach((unusedValue, dateKey) => {
    pendingDates.delete(dateKey);
  });

  const sumDateValues = (dateMap) =>
    [...dateMap.values()].reduce((total, value) => total + value, 0);

  const taken = sumDateValues(approvedDates);
  const pending = sumDateValues(pendingDates);
  const takenBreakdown = [...approvedDates.entries()]
    .map(([date, days]) => ({
      date,
      days,
      firebaseIds: [
        ...(approvedRequestIdsByDate.get(date) || new Set()),
      ].sort(),
    }))
    .sort((left, right) => left.date.localeCompare(right.date));

  const remaining =
    balance.entitlement +
    balance.carriedForward +
    balance.extraDays -
    balance.borrowedFromNextYear -
    taken -
    pending;

  return {
    entitlement: balance.entitlement,
    annualEntitlement: balance.annualEntitlement,
    taken,
    takenBreakdown,
    pending,
    remaining,

    employmentStartDate: balance.employmentStartDate,
    employmentEndDate: balance.employmentEndDate,
    effectiveStartDate: balance.effectiveStartDate,
    effectiveEndDate: balance.effectiveEndDate,
    employedDaysInHolidayYear: balance.employedDaysInHolidayYear,
    totalDaysInHolidayYear: balance.totalDaysInHolidayYear,
  };
};

export const buildLeaveStatCards = (stats = {}) => [
  {
    id: "entitlement",
    label: "Entitlement",
    value: formatNumber(stats.entitlement),
    description: "Annual leave allowance",
    tone: "blue",
  },
  {
    id: "taken",
    label: "Taken",
    value: formatNumber(stats.taken),
    description: "Approved annual leave",
    tone: "cream",
  },
  {
    id: "pending",
    label: "Pending",
    value: formatNumber(stats.pending),
    description: "Awaiting approval",
    tone: "purple",
  },
  {
    id: "remaining",
    label: "Remaining",
    value: formatNumber(stats.remaining),
    description: "Available balance",
    tone: "green",
  },
];

export const getStaffHolidayProfile = (
  staff = {},
  requests = [],
  selectedYear,
) => {
  const departments = normalizeDepartments(staff.departments);
  const departmentName = getDepartmentName(staff);
  const holidayBalance = normalizeHolidayBalance(staff, selectedYear);
  const trackerHistory = buildTrackerHistoryFromEvents(
    staff.importedTrackerEvents ||
      staff.trackerEvents ||
      staff.leaveEvents ||
      [],
    staff,
    selectedYear,
  );

  const combinedRequestsForStats = [...requests, ...trackerHistory];

  const bradfordBreakdown = getBradfordFactorBreakdown(
    combinedRequestsForStats,
    selectedYear,
  );
  const leaveStats = getAnnualLeaveStats({
    staff,
    requests,
    selectedYear,
  });

  const statCards = buildLeaveStatCards(leaveStats);

  const isEmployedInHolidayYear = isStaffEmployedInHolidayYear(
    staff,
    selectedYear,
  );

  return {
    departments,
    departmentName,

    holidayBalance,
    leaveStats,
    statCards,

    isEmployedInHolidayYear,

    employmentStartDate: holidayBalance.employmentStartDate,
    employmentEndDate: holidayBalance.employmentEndDate,
    effectiveStartDate: holidayBalance.effectiveStartDate,
    effectiveEndDate: holidayBalance.effectiveEndDate,
    trackerHistory,
    trackerEntries: trackerHistory,
    trackerEntryCount: trackerHistory.length,
    bradfordFactor: bradfordBreakdown.bradfordFactor,
    sicknessInstances: bradfordBreakdown.sicknessInstances,
    sicknessDays: bradfordBreakdown.sicknessDays,
    annualEntitlement: holidayBalance.annualEntitlement,
    entitlement: leaveStats.entitlement,
    remaining: leaveStats.remaining,
    taken: leaveStats.taken,
    pending: leaveStats.pending,
  };
};

export const normalizeStaffDirectoryItem = (item = {}, selectedYear) => {
  const email = normaliseEmail(item.email || item.id);
  const holidayProfile = getStaffHolidayProfile(item, [], selectedYear);

  return {
    ...item,

    email,
    displayName: getStaffDirectoryDisplayName(item),

    holidayProfile,
    bradfordFactor: holidayProfile.bradfordFactor,
    sicknessInstances: holidayProfile.sicknessInstances,
    sicknessDays: holidayProfile.sicknessDays,
    departments: holidayProfile.departments,
    departmentName: holidayProfile.departmentName,

    employmentStartDate: holidayProfile.employmentStartDate,
    employmentEndDate: holidayProfile.employmentEndDate,

    holidayBalance: holidayProfile.holidayBalance,
    leaveStats: holidayProfile.leaveStats,
    statCards: holidayProfile.statCards,

    isEmployedInHolidayYear: holidayProfile.isEmployedInHolidayYear,

    leaveBalance: item.leaveBalance || item.holidayBalance || item.balance,
    balance: item.balance || item.holidayBalance || item.leaveBalance,
  };
};

export const buildStaffHolidayProfiles = ({
  staffDirectory = [],
  requests = [],
  selectedYear,
}) => {
  const map = new Map();

  staffDirectory.forEach((staff) => {
    const email = normaliseEmail(staff.email || staff.id);

    if (!email) return;

    const holidayProfile = getStaffHolidayProfile(staff, [], selectedYear);

    map.set(email, {
      ...staff,
      email,
      name: getStaffDirectoryDisplayName(staff),

      holidayProfile,

      departmentName: holidayProfile.departmentName,
      departments: holidayProfile.departments,
      holidayBalance: holidayProfile.holidayBalance,
      leaveStats: holidayProfile.leaveStats,
      statCards: holidayProfile.statCards,
      isEmployedInHolidayYear: holidayProfile.isEmployedInHolidayYear,
      trackerHistory: holidayProfile.trackerHistory,
      trackerEntries: holidayProfile.trackerEntries,
      trackerEntryCount: holidayProfile.trackerEntryCount,
      bradfordFactor: holidayProfile.bradfordFactor,
      sicknessInstances: holidayProfile.sicknessInstances,
      sicknessDays: holidayProfile.sicknessDays,
      requests: [],
    });
  });

  requests.forEach((request) => {
    const email = getRequestStaffEmail(request);

    if (!email) return;

    if (!map.has(email)) {
      const holidayProfile = getStaffHolidayProfile(request, [], selectedYear);

      map.set(email, {
        ...request,
        email,
        name: getRequestStaffName(request),

        holidayProfile,

        departmentName: holidayProfile.departmentName,
        departments: holidayProfile.departments,
        holidayBalance: holidayProfile.holidayBalance,
        leaveStats: holidayProfile.leaveStats,
        statCards: holidayProfile.statCards,
        isEmployedInHolidayYear: holidayProfile.isEmployedInHolidayYear,

        requests: [],
      });
    }

    const existingStaff = map.get(email);

    const existingDepartmentName =
      existingStaff.departmentName &&
      existingStaff.departmentName !== "No department"
        ? existingStaff.departmentName
        : getDepartmentName(request);

    const existingDepartments =
      existingStaff.departments?.length > 0
        ? existingStaff.departments
        : normalizeDepartments(request.departments);

    map.set(email, {
      ...existingStaff,
      departmentName: existingDepartmentName,
      departments: existingDepartments,
      requests: [...existingStaff.requests, request],
    });
  });

  return Array.from(map.values())
    .map((staff) => {
      const actionableCount = staff.requests.filter((request) =>
        isPendingApprovalStatus(request.status),
      ).length;

      const approvedCount = staff.requests.filter((request) =>
        isApprovedStatus(request.status),
      ).length;

      const rejectedCount = staff.requests.filter((request) =>
        isRejectedStatus(request.status),
      ).length;

      const cancelledCount = staff.requests.filter((request) =>
        isCancelledStatus(request.status),
      ).length;

      const holidayProfile = getStaffHolidayProfile(
        staff,
        staff.requests,
        selectedYear,
      );

      return {
        ...staff,

        actionableCount,
        approvedCount,
        rejectedCount,
        cancelledCount,

        holidayProfile,
        bradfordFactor: holidayProfile.bradfordFactor,
        sicknessInstances: holidayProfile.sicknessInstances,
        sicknessDays: holidayProfile.sicknessDays,

        holidayBalance: holidayProfile.holidayBalance,
        leaveStats: holidayProfile.leaveStats,
        statCards: holidayProfile.statCards,
        isEmployedInHolidayYear: holidayProfile.isEmployedInHolidayYear,

        totalDays: staff.requests.reduce(
          (acc, request) => acc + toNumber(request.totalDays, 0),
          0,
        ),
      };
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

export const buildOverallLeaveStats = (staffProfiles = []) => {
  return staffProfiles.reduce(
    (totals, staff) => ({
      entitlement:
        totals.entitlement + toNumber(staff.leaveStats?.entitlement, 0),
      taken: totals.taken + toNumber(staff.leaveStats?.taken, 0),
      pending: totals.pending + toNumber(staff.leaveStats?.pending, 0),
      remaining: totals.remaining + toNumber(staff.leaveStats?.remaining, 0),
    }),
    {
      entitlement: 0,
      taken: 0,
      pending: 0,
      remaining: 0,
    },
  );
};

export const hasStaffDepartment = (staff = {}) => {
  const departmentName = String(staff.departmentName || "").trim();

  if (!departmentName) return false;
  if (departmentName.toLowerCase() === "no department") return false;

  const departments = normalizeDepartments(staff.departments);

  return Boolean(departmentName || departments.length > 0);
};

export const isStaffEmployedInHolidayYear = (
  staff = {},
  selectedYear = new Date().getFullYear(),
) => {
  const { yearStart, yearEnd } = getHolidayYearRange({
    holidayYear: selectedYear,
  });

  const employmentStartDate = parseHolidayDate(
    getStaffEmploymentStartDate(staff),
  );
  const employmentEndDate = parseHolidayDate(getStaffEmploymentEndDate(staff));

  if (employmentStartDate && employmentStartDate > yearEnd) return false;
  if (employmentEndDate && employmentEndDate < yearStart) return false;

  return true;
};

const normalizeLeaveTypeValue = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll(" ", "_")
    .replaceAll("-", "_");
};

const SICKNESS_LEAVE_TYPE_VALUES = [
  "sick",
  "sickness",
  "sick_leave",
  "sickness_leave",
  "sickleave",
  "sick_leave_paid",
];

export const isSicknessLeaveRequest = (request = {}) => {
  const leaveType = normalizeLeaveTypeValue(request.leaveType);
  const status = String(request.status || "")
    .trim()
    .toLowerCase();

  return (
    SICKNESS_LEAVE_TYPE_VALUES.includes(leaveType) &&
    status === LEAVE_STATUS.APPROVED
  );
};

const countsTowardsBradfordFactor = (request = {}) => {
  if (isSicknessLeaveRequest(request)) return true;
  if (
    !isApprovedStatus(request.status) ||
    normalizeLeaveTypeValue(request.leaveType) !== LEAVE_TYPES.ANNUAL_LEAVE
  ) return false;

  // A change to annual leave does not erase the recorded sickness history.
  const auditTrail = Array.isArray(request.auditTrail) ? request.auditTrail : [];
  return auditTrail.some((audit) =>
    [audit?.before?.leaveType, audit?.after?.leaveType].some((leaveType) =>
      SICKNESS_LEAVE_TYPE_VALUES.includes(normalizeLeaveTypeValue(leaveType)),
    ),
  );
};

const TRACKER_IMPORTED_SOURCE = "excel_import";

const isImportedTrackerEvent = (event = {}) => {
  return (
    event.source === TRACKER_IMPORTED_SOURCE ||
    Boolean(event.importedFrom) ||
    Boolean(event.importedYear)
  );
};

const toDateOnly = (value) => {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const toDateKeyFromDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getNextWorkingDateKey = (dateKey) => {
  const date = toDateOnly(dateKey);

  if (!date) return "";

  const next = new Date(date);
  next.setDate(next.getDate() + 1);

  while ([0, 6].includes(next.getDay())) {
    next.setDate(next.getDate() + 1);
  }

  return toDateKeyFromDate(next);
};

export const buildTrackerHistoryFromEvents = (
  events = [],
  staff = {},
  selectedYear,
) => {
  const importedEvents = (events || [])
    .filter(isImportedTrackerEvent)
    .filter((event) => {
      const eventYear =
        Number(event.importedYear) ||
        Number(String(event.date || "").slice(0, 4));

      return !selectedYear || eventYear === Number(selectedYear);
    })
    .filter((event) => event.date && event.leaveType)
    .sort((a, b) => {
      const dateSort = String(a.date).localeCompare(String(b.date));

      if (dateSort !== 0) return dateSort;

      return String(a.leaveType).localeCompare(String(b.leaveType));
    });

  const grouped = [];

  importedEvents.forEach((event) => {
    const last = grouped[grouped.length - 1];
    const date = String(event.date || "");
    const leaveType = String(event.leaveType || "");
    const dayValue = Number(event.dayValue || 0);

    const shouldExtendLast =
      last &&
      last.leaveType === leaveType &&
      getNextWorkingDateKey(last.endDate) === date;

    if (shouldExtendLast) {
      last.endDate = date;
      last.totalDays += dayValue;
      last.trackerEvents.push(event);
      return;
    }

    grouped.push({
      id: `tracker_${staff.email || event.staffEmail}_${leaveType}_${date}`,
      requestId: `tracker_${staff.email || event.staffEmail}_${leaveType}_${date}`,

      staffEmail: staff.email || event.staffEmail || "",
      staffName:
        staff.displayName ||
        staff.name ||
        event.staffName ||
        staff.email ||
        event.staffEmail ||
        "Unknown staff",

      departmentId: staff.departmentId || event.departmentId || "",
      departmentName: staff.departmentName || event.departmentName || "",

      leaveType,
      startDate: date,
      endDate: date,
      totalDays: dayValue,
      status: "approved",

      source: "tracker_import",
      importedFrom: event.importedFrom || "",
      importedYear: event.importedYear || selectedYear || "",

      isTrackerImport: true,
      trackerEvents: [event],
    });
  });

  return grouped;
};

export const getBradfordFactorBreakdown = (requests = [], selectedYear) => {
  // Calculate per staff member, using dates rather than imported record counts.
  const sicknessByDate = new Map();
  const addDate = (value, days) => {
    const date = toDateKey(value);
    if (!date || (selectedYear && Number(date.slice(0, 4)) !== Number(selectedYear))) return;
    const dayValue = Math.min(1, Math.max(0, toNumber(days, 0)));
    if (!dayValue) return;
    sicknessByDate.set(date, Math.max(sicknessByDate.get(date) || 0, dayValue));
  };

  requests.filter(countsTowardsBradfordFactor).forEach((request) => {
    const recordedDates = request.trackerEvents?.length
      ? request.trackerEvents
      : request.includedDates;
    if (Array.isArray(recordedDates) && recordedDates.length) {
      recordedDates.forEach((item) => {
        if (item?.status && !isApprovedStatus(item.status)) return;
        addDate(
          item?.date || item?.dateKey || item,
          item?.dayValue ?? item?.value ?? item?.days ?? item?.totalDays ?? 1,
        );
      });
      return;
    }

    const startDate = parseHolidayDate(request.startDate || request.date);
    const endDate = parseHolidayDate(request.endDate || request.startDate || request.date);
    if (!startDate || !endDate || endDate < startDate) return;
    const dates = getDatesBetween(startDate, endDate).filter((date) => !isWeekend(date));
    let remainingDays = toNumber(request.totalDays ?? request.dayValue, dates.length);
    dates.forEach((date, index) => {
      const halfDay =
        (index === 0 && request.startDayType && request.startDayType !== "full") ||
        (index === dates.length - 1 && request.endDayType && request.endDayType !== "full");
      const days = Math.min(halfDay ? 0.5 : 1, remainingDays);
      addDate(date, days);
      remainingDays -= Math.max(0, days);
    });
  });

  const dates = [...sicknessByDate.keys()].sort();
  const sicknessInstances = dates.reduce((count, date, index) => {
    const previous = dates[index - 1];
    return count + (!previous || date > getNextWorkingDateKey(previous) ? 1 : 0);
  }, 0);
  const sicknessDays = [...sicknessByDate.values()].reduce((total, days) => total + days, 0);

  const bradfordFactor = sicknessInstances * sicknessInstances * sicknessDays;

  return {
    bradfordFactor,
    sicknessInstances,
    sicknessDays,
  };
};
